import {
  DataResult,
  State as DataState,
  FilterOperator,
} from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProductStatusTypes } from '../../../../common/enums';
import { DataLoader } from '../../../../common/widgets/DataLoader';
import Grid, { getSkip, GridCellTd } from '../../../../common/widgets/Grid';
import Icons from '../../../../common/widgets/Icons';
import { config } from '../../config';
import { lowStockThreshold, productStatusColor } from '../../constants';
import { ProductTypes } from '../../enums';
import { ProductItemModel } from '../../models/products';
import { resources } from '../../resources/resources';
import { productsService } from '../../services/products';
import { getStockCell } from '../Products/Common/List';

const initialDataState: DataState = {
  skip: 0,
  take: config.grid.defaultPageSize,
  filter: {
    logic: 'and',
    filters: [
      {
        field: 'stock',
        operator: FilterOperator.LessThanOrEqual,
        value: lowStockThreshold,
      },
      {
        logic: 'or',
        filters: [
          {
            field: 'productTypeId',
            operator: FilterOperator.EqualTo,
            value: ProductTypes.ProductOrService,
          },
          {
            field: 'productTypeId',
            operator: FilterOperator.EqualTo,
            value: ProductTypes.GiftCard,
          },
        ],
      },
      {
        logic: 'or',
        filters: [
          {
            field: 'statusId',
            operator: FilterOperator.EqualTo,
            value: ProductStatusTypes.Active,
          },
          {
            field: 'statusId',
            operator: FilterOperator.EqualTo,
            value: ProductStatusTypes.Draft,
          },
          {
            field: 'statusId',
            operator: FilterOperator.EqualTo,
            value: ProductStatusTypes.PendingApproval,
          },
        ],
      },
    ],
  },
  sort: [
    { field: 'status', dir: 'asc' },
    { field: 'stock', dir: 'asc' },
  ],
};

export const LowStockProducts = () => {
  const [products, setProducts] = useState<ProductItemModel[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [dataState, setDataState] = useState<DataState>(initialDataState);
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  const onDataReceived = (data: DataResult, showErrorPanel?: boolean) => {
    if (!showErrorPanel) {
      setProducts(data.data);
      setTotal(data.total);
    } else {
      setProducts(null);
      setTotal(0);
    }
  };

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState({ ...e.dataState, skip: getSkip(dataState, e.dataState) });
  };

  return (
    <>
      <DataLoader
        getData={productsService.getProducts}
        dataState={dataState}
        onDataReceived={onDataReceived}
      />
      {!!products?.length && (
        <div className="m-panel">
          <h1>{translate(resources.home.lowStock.title)}</h1>
          <div className="m-grid m-products-grid">
            <Grid
              data={products}
              pageable={{
                pageSizes: config.grid.pageSizes,
                pageSizeValue: dataState.take,
                info: false,
                previousNext: true,
              }}
              {...dataState}
              onDataStateChange={onDataStateChange}
              total={total}
              sortable={{ allowUnsort: true, mode: 'multiple' }}
              columns={[
                {
                  field: 'name',
                  title: translate(resources.products.list.columns.name),
                },
                {
                  field: 'status',
                  title: translate(resources.products.list.columns.status),
                  cell: ({ dataItem }: GridCellProps) => (
                    <GridCellTd className="grid-center">
                      <span
                        style={{
                          backgroundColor:
                            productStatusColor[
                              dataItem.statusId as ProductStatusTypes
                            ],
                        }}
                        className="m-grid-badge"
                      >
                        {dataItem.status}
                      </span>
                    </GridCellTd>
                  ),
                  headerClassName: 'grid-center',
                },
                {
                  field: 'categories',
                  title: translate(resources.products.list.columns.categories),
                  sortable: false,
                },
                {
                  field: 'productType',
                  title: translate(resources.products.list.columns.productType),
                  sortable: false,
                },
                {
                  field: 'stock',
                  title: translate(resources.products.list.columns.stock),
                  cell: ({ dataItem }: GridCellProps) => getStockCell(dataItem),
                  headerClassName: 'grid-center',
                },
                {
                  title: translate(resources.actions),
                  cell: ({ dataItem }) => (
                    <GridCellTd className="grid-center">
                      <button
                        onClick={() =>
                          navigate(`/products/add/codes/${dataItem.productId}`)
                        }
                        className="btn-link"
                      >
                        <span className="btn-icon">
                          <Icons.ArrowButton />
                        </span>
                        <span className="btn-text">
                          {translate(resources.home.lowStock.addStock)}
                        </span>
                      </button>
                    </GridCellTd>
                  ),
                  headerClassName: 'grid-center',
                  sortable: false,
                },
              ]}
            />
          </div>
        </div>
      )}
    </>
  );
};
